// Notification service for push notifications
import toast from 'react-hot-toast';

interface ExtendedNotificationOptions extends NotificationOptions {
  targetConversationId?: string;
  activeConversationId?: string;
}

class NotificationService {
  private permission: NotificationPermission = 'default';
  private isSupported: boolean;

  constructor() {
    this.isSupported = 'Notification' in window;
    if (this.isSupported) {
      this.permission = Notification.permission;
    }
  }

  /**
   * Request permission for notifications
   */
  async requestPermission(): Promise<NotificationPermission> {
    if (!this.isSupported) {
      if (process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true') {
        console.warn('Notifications are not supported in this browser');
      }
      return 'denied';
    }

    if (this.permission === 'granted') {
      return 'granted';
    }

    try {
      this.permission = await Notification.requestPermission();
      return this.permission;
    } catch (error) {
      if (process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true') {
        console.error('Error requesting notification permission:', error);
      }
      return 'denied';
    }
  }

  /**
   * Check if the page is currently visible
   */
  private isPageVisible(): boolean {
    return !document.hidden;
  }

  /**
   * Show a notification.
   * Suppressed when the page is visible UNLESS:
   *  - forceShow is true, OR
   *  - the notification targets a different conversation than the one currently active
   */
  showNotification(title: string, options?: ExtendedNotificationOptions, forceShow: boolean = false): Notification | null {
    if (!this.isSupported || this.permission !== 'granted') {
      return null;
    }

    const isForDifferentConversation =
      options?.targetConversationId &&
      options?.activeConversationId &&
      options.targetConversationId !== options.activeConversationId;

    if (!forceShow && !isForDifferentConversation && this.isPageVisible()) {
      if (process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true') {
        console.log('Notification suppressed: page is currently visible and same conversation');
      }
      return null;
    }

    try {
      const notification = new Notification(title, {
        icon: '/favicon.ico',
        badge: '/favicon.ico',
        ...options,
      });

      // Auto-close notification after 5 seconds
      setTimeout(() => {
        notification.close();
      }, 5000);

      return notification;
    } catch (error) {
      if (process.env.REACT_APP_ENABLE_DEBUG_LOGS === 'true') {
        console.error('Error showing notification:', error);
      }
      return null;
    }
  }

  /**
   * Show a message notification
   */
  showMessageNotification(senderName: string, message: string, conversationId?: string): Notification | null {
    return this.showNotification(`New message from ${senderName}`, {
      body: message,
      tag: `message-${conversationId || 'unknown'}`,
      data: { conversationId }
    });
  }

  /**
   * Show a group message notification
   */
  showGroupMessageNotification(groupName: string, senderName: string, message: string, groupId?: number): Notification | null {
    return this.showNotification(`${groupName}`, {
      body: `${senderName}: ${message}`,
      tag: `group-message-${groupId || 'unknown'}`,
      data: { groupId, conversationId: `group_${groupId}` }
    });
  }

  /**
   * Check if notifications are supported
   */
  isNotificationSupported(): boolean {
    return this.isSupported;
  }

  /**
   * Get current permission status
   */
  getPermission(): NotificationPermission {
    return this.permission;
  }

  /**
   * Check if notifications are enabled
   */
  isEnabled(): boolean {
    return this.isSupported && this.permission === 'granted';
  }

  /**
   * Show a toast notification
   */
  showToast(message: string, type: 'success' | 'error' | 'warning' | 'info' = 'info'): string {
    switch (type) {
      case 'success':
        return toast.success(message);
      case 'error':
        return toast.error(message);
      case 'warning':
        return toast(message, {
          icon: '⚠️',
          style: {
            background: '#fbbf24',
            color: '#92400e',
          },
        });
      case 'info':
      default:
        return toast(message, {
          icon: 'ℹ️',
        });
    }
  }
}

// Export singleton instance
export const notificationService = new NotificationService();
export default notificationService;